/**
 * **What a finished session says about itself on a feedback card** — the ring, the meta line and
 * the row of signal chips, decided in one place.
 *
 * One entry point rather than four, because the card does not want four answers: it wants the
 * session's readout. A component calling `sessionRing`, then a mood rule, then a chip filter, then
 * a sentence join is making the composition decision itself, in JSX, where nothing can assert it.
 *
 * Two reads feed it. The **queue payload** carries the fraction and the duration, so the ring and
 * the meta line are drawn on the card's first paint (attention-queue R11);
 * `GET /workout-execution/{id}/display` arrives later with the chips and the sleep figure.
 *
 * ── The chips count SETS, all of them ────────────────────────────────────────────────────────
 * `weightUpCount` is the exact mirror of `weightDownCount` on the same granularity. The alternative
 * for "up" is `exercises[].vsLastTime.direction`, which counts EXERCISES and answers a different
 * question ("versus last time") — two units in one row of chips is a row the trainer reads wrong.
 *
 * ── At most THREE chips, on ONE line ─────────────────────────────────────────────────────────
 * Owner, 16 September 2026: a good session drew five chips, which wrapped onto a second line and
 * stopped being a row of signals — it became a list. What survives the cut is the mood, ONE signal
 * standing for the session's dominant side, and a neutral counter for whatever is left.
 *
 * The sides are **bad** (`skipped`, `weightDown`) and **good** (`record`, `weightUp`), and the one
 * that dominates is the one with the larger SUM OF OCCURRENCES — not the larger number of kinds, so
 * seven skipped sets outweigh one record and one heavier set rather than losing two-to-one to them.
 * Within the winning side the stronger signal speaks: `skipped` over `weightDown`, `record` over
 * `weightUp`.
 *
 * **A tie goes to the BAD side.** The owner's decision, and the reason is what this card IS: a queue
 * of things wanting the trainer, not a summary of the session. The backend reads it the same way —
 * it has a notion of a session carrying negative flags and no positive counterpart to it.
 *
 * ── A signal that is not there draws NO chip ─────────────────────────────────────────────────
 * `completed-session-display` R11: subjective inputs render only when present, and an absent one
 * shows no empty placeholder — never a chip reading zero, which would say the client logged the
 * worst possible mood.
 *
 * Mood is the one chip drawn even when the news is good, which is the same rule read forwards: if a
 * high mood were omitted, an absent chip would mean "did not say" and "said something good" at once.
 * The colour is what changes.
 *
 * Pure: no React, no DOM, no query hook, no i18next. `t` and the number formatter arrive as
 * parameters.
 */

import type { TrainWithMeModulesTrainingDtosResponsesWorkoutExecutionDisplayResponse as WorkoutExecutionDisplayResponse } from '~/shared/api/generated/model';
import type { Translate } from '~/shared/lib/localeTranslate';

import type { SessionFeedback } from './feedbackShape';
import { sessionRing, type SessionRing } from './ringInk';

const SECONDS_PER_MINUTE = 60;

/**
 * How a chip's GLYPH is inked. Everything else about the chip — the words and the figure — is
 * neutral.
 *
 * It was the numeral that took the colour until 16 September 2026. The owner moved it to the mark:
 * a red arrow down, a green arrow up, a lime trophy, with the count left grey. It is also the
 * easier position to hold — a glyph is a graphic and owes 3:1, where 11.5px of bold text owes 4.5.
 */
export type SignalInk = 'neutral' | 'warn' | 'danger' | 'good' | 'record';

/** Which signal a chip is about. It also names the chip's drawing; `ui/` owns the drawing. */
export type SignalId = 'mood' | 'skipped' | 'weightDown' | 'weightUp' | 'record';

/**
 * What a chip in the row can be: one of the five signals, or the counter standing for the ones the
 * three-chip cap left out.
 *
 * `more` is deliberately NOT a `SignalId`. It is not a thing the client did, it carries no glyph,
 * and giving it one would have obliged `ui/HomeGlyphs`' exhaustive mark table to invent a drawing
 * for an absence.
 */
export type ChipId = SignalId | 'more';

/**
 * One chip, in three runs — because the FIGURE is set apart from the words around it: extra-bold
 * and tabular, so a column of chips lines up on its digits. (The colour is not what splits it. It
 * goes on the GLYPH and on nothing else since 16 September 2026 — `SignalInk` above says so, and
 * so does `docs/design/README.md`.) The sentence therefore cannot arrive as one string, and it
 * cannot be assembled in JSX either: "ciężar w dół · 3 serie" and "3 rekordy" put the number in
 * different places, and Polish picks the noun's form off it.
 *
 * The split happens where the words are resolved — the copy carries a `{{value}}` slot, `t` selects
 * the plural form off `count`, and the resolved template is cut at the slot. Meaning stays in the
 * WORD and the GLYPH, so the row reads identically in greyscale.
 */
export interface SessionSignal {
  readonly id: ChipId;
  /** Everything before the figure, already in the reader's language. Often `''`. */
  readonly before: string;
  /** The figure itself — the one run drawn extra-bold and tabular, in the chip's own grey. */
  readonly value: string;
  /** Everything after it. Often the counted noun. */
  readonly after: string;
  /** The GLYPH's ink. The words and the figure never take it — see `SignalInk`. */
  readonly ink: SignalInk;
  /**
   * What the counter hides, named in full — `': ciężar w dół · 1 seria, 2 rekordy'`.
   *
   * `''` for every real signal, and set only on `more`. It is the counter's own accessible tail
   * rather than a second hidden block somewhere on the row: three of the five signals stopped being
   * words on screen when the cap landed, and an `sr-only` paragraph nobody navigates to would have
   * been a place to put them rather than a way to hear them. Read where the counter is read, after
   * the words it belongs to — which is also why it begins with its own punctuation.
   */
  readonly detail: string;
}

export interface SessionReadout {
  /**
   * The completion ring, or `null` where none may be drawn.
   *
   * `null` for a freestyle session by rule — `completed-session-display` R9 forbids an `AdHoc`
   * verdict referencing compliance against a prescription, and its denominator is whatever the
   * client added themselves — and `null` for any session whose counted sets the read could not
   * count.
   */
  readonly ring: SessionRing | null;
  /** The ring's accessible name and pointer title, in one sentence. `''` where there is no ring. */
  readonly ringLabel: string;
  /**
   * **The one big number in the row's left slot** — the sets the client actually did. `''` where the
   * wire carried no count at all, which is the only case the slot is not drawn.
   *
   * The same figure for both shapes, which is the point: a planned session rings it and a freestyle
   * one does not, and that arc is the **whole** difference between the two rows. Owner, 16 September
   * 2026 — freestyle used to state its count in the meta line instead, which moved the row's content
   * sideways and made two layouts out of one.
   *
   * The NUMERATOR alone, never the fraction. `22 z 24` measures 45.6px at `--fs-ring-figure` and the
   * 62px ring's hole is 45.6px wide where a 15px line crosses it, so the whole fraction sat on the
   * arc; the denominator is in the caption under it.
   */
  readonly figure: string;
  /**
   * The words under the figure: `z 24 serii` where a prescription set the denominator, `serii`
   * where the client set it themselves. `''` whenever `figure` is.
   *
   * A freestyle session may not state a fraction at all (`completed-session-display` R9), so its
   * caption is the unit and nothing more — the figure above it is the whole statement.
   */
  readonly caption: string;
  /**
   * **What the session was and how long it ran** — `Siła A · 61 min`.
   *
   * Either half may be missing and the line then states the other alone; both missing is `''` and
   * the element draws nothing rather than a hanging separator. A freestyle session never has a
   * name, so its line is the duration, which is what it has always been.
   */
  readonly meta: string;
  readonly signals: readonly SessionSignal[];
  /**
   * EVERY signal the session had, uncapped, in the row's own order — the `/zadania` card and the
   * Talia card (KANON "Karty zadań", 24 September 2026), which have the width for all of them.
   * The three-chip cap above is the Pulpit row's, where the chips share one line with the ring.
   */
  readonly allSignals: readonly SessionSignal[];
}

const COPY = 'web:home.feedback';

/** Mood runs 1–10 with 10 best (`[Range(1, 10)]` on the write). At or below this it reads as low. */
const LOW_MOOD = 4;

/**
 * A marker handed to `t` in place of a chip's figure, so the resolved sentence can be cut where the
 * figure belongs.
 *
 * A control character rather than `{{value}}` itself: i18next's interpolator runs until nothing
 * matches, so substituting a placeholder for itself would loop against its own `maxReplaces` guard.
 * U+0001 cannot appear in copy — it is not typeable, and the bundles are JSON written by hand.
 */
const VALUE_SLOT = '';

/**
 * The row's cap, and what fills it: the mood, one dominant signal, and the counter.
 *
 * Not a number to tune. Each of the three slots is a different KIND of statement, which is why a
 * fourth chip has nowhere to come from — see the header.
 */
const ROW_ORDER = ['skipped', 'weightDown', 'weightUp', 'record'] as const;

type CountedId = (typeof ROW_ORDER)[number];

/** Which side each counted signal falls on, and the ink it carries when it is the one drawn. */
const INK_OF: Readonly<Record<CountedId, SignalInk>> = {
  // The whole BAD side is `--danger`, both of its signals: they differ by their glyph — a warning
  // mark against an arrow pointing down — and by their word, so a second colour would be a third
  // encoding of one fact. The good side splits because its two signals are different KINDS of good.
  skipped: 'danger',
  weightDown: 'danger',
  weightUp: 'good',
  record: 'record',
};

/**
 * Between the counter's visible words and the signals it stands for, and between those.
 *
 * Punctuation, not copy — the same in either language, for the reason `META_SEPARATOR` is.
 */
const HIDDEN_PREFIX = ': ';
const HIDDEN_SEPARATOR = ', ';

/** Between the meta line's two halves. Punctuation, not copy — the same in either language. */
const META_SEPARATOR = ' · ';

export function sessionReadout(
  session: SessionFeedback,
  display: WorkoutExecutionDisplayResponse | null | undefined,
  t: Translate,
): SessionReadout {
  const ring = session.freestyle ? null : sessionRing(session.setsDone, session.setsTotal);

  return {
    ring,
    ringLabel:
      ring === null
        ? ''
        : t(`${COPY}.session.ring`, { done: ring.done, total: ring.total, percent: ring.percent }),
    ...slotOf(session, ring, t),
    meta: metaOf(session, t),
    signals: signalsOf(display, t),
    allSignals: allSignalsOf(display, t),
  };
}

/**
 * The row's left slot: the figure, and the words under it.
 *
 * Both shapes print the sets DONE. What changes is the caption — a planned session names the
 * denominator its plan set, a freestyle one names only the unit, because its denominator is
 * whatever the client added themselves and a fraction over it answers a question nobody asked (R9).
 */
function slotOf(
  session: SessionFeedback,
  ring: SessionRing | null,
  t: Translate,
): Pick<SessionReadout, 'figure' | 'caption'> {
  if (ring !== null) {
    return {
      figure: String(ring.done),
      caption: t(`${COPY}.session.ringCaption`, { count: ring.total }),
    };
  }

  // `setsDone` is what they completed, `setsTotal` what they created — both are counts of their own
  // work, and `done` is the one that says what happened rather than what was started.
  const logged = session.freestyle ? session.setsDone ?? session.setsTotal : null;
  if (logged === null) return { figure: '', caption: '' };

  return { figure: String(logged), caption: t(`${COPY}.session.setsCaption`, { count: logged }) };
}

/**
 * The line beside the slot: **which session this was, and how long it took.**
 *
 * It held the set fraction and the sleep figure until 16 September 2026, and a freestyle session's
 * set count until later the same day. The counts are the slot's now — stated once, in the same place
 * on both shapes — and the sleep figure went entirely on the owner's word: it is a fact about the
 * night before rather than about the session, and the client's own check-in is where it is read.
 *
 * That left a planned session's line reading `61 min` and nothing else, which is thinner than the
 * slot beside it. The **name** joined it later the same day. It is not the same decision as the
 * row's SENTENCE, which deliberately states the event and interpolates no name (`model/attentionSections`
 * — "Siła 5×5" under a client's name says nothing about what happened to it); here the line is a
 * list of facts about the session, and which session it was is the first of them.
 *
 * The halves are joined rather than templated, so a missing one costs the line nothing: a nameless
 * session prints the duration alone and an untimed one prints the name alone, with no separator
 * either side of an empty run.
 */
function metaOf(session: SessionFeedback, t: Translate): string {
  const minutes =
    session.durationSeconds === null ? 0 : Math.round(session.durationSeconds / SECONDS_PER_MINUTE);

  // A session too short to round to a minute states no duration rather than "0 min", which reads as
  // a broken timer rather than as a two-minute session.
  const duration = minutes <= 0 ? '' : t(`${COPY}.session.minutes`, { count: minutes });

  return [session.name ?? '', duration].filter((half) => half !== '').join(META_SEPARATOR);
}

/**
 * The chip row: the mood, the session's dominant signal, and a neutral counter for the rest.
 *
 * At most three, always one line. Which signal is the dominant one, and why a tie goes to the bad
 * side, is the header.
 */
function signalsOf(
  display: WorkoutExecutionDisplayResponse | null | undefined,
  t: Translate,
): readonly SessionSignal[] {
  const inputs = display?.verdictInputs;
  const counts: Record<CountedId, number> = {
    skipped: countOf(inputs?.skippedCount),
    weightDown: countOf(inputs?.weightDownCount),
    weightUp: countOf(inputs?.weightUpCount),
    record: countOf(inputs?.prCount),
  };

  const row: SessionSignal[] = [];

  const mood = display?.context?.mood;
  if (typeof mood === 'number' && Number.isFinite(mood)) {
    row.push(chipOf(t, 'mood', mood, mood <= LOW_MOOD ? 'warn' : 'neutral'));
  }

  const dominant = dominantOf(counts);
  // Nothing counted happened beyond the mood — a real session, and a common one.
  if (dominant === null) return row;
  row.push(chipOf(t, dominant, counts[dominant], INK_OF[dominant]));

  const hidden = ROW_ORDER.filter((id) => id !== dominant && counts[id] > 0).map((id) =>
    sentenceOf(chipOf(t, id, counts[id], INK_OF[id])),
  );
  // Exactly one counted signal, so the dominant chip already IS all of them and a counter would be
  // a chip saying nothing was left out.
  if (hidden.length === 0) return row;

  row.push({
    // Neutral and with no glyph: on this card colour marks a signal, and how many signals were left
    // out is not one. The counter's figure therefore reads in the chip's own `--text-2`.
    ...chipOf(t, 'more', hidden.length, 'neutral'),
    detail: HIDDEN_PREFIX + hidden.join(HIDDEN_SEPARATOR),
  });

  return row;
}

/** The mood, then every counted signal the session had, in `ROW_ORDER`. No cap, no counter. */
function allSignalsOf(
  display: WorkoutExecutionDisplayResponse | null | undefined,
  t: Translate,
): readonly SessionSignal[] {
  const inputs = display?.verdictInputs;
  const counts: Record<CountedId, number> = {
    skipped: countOf(inputs?.skippedCount),
    weightDown: countOf(inputs?.weightDownCount),
    weightUp: countOf(inputs?.weightUpCount),
    record: countOf(inputs?.prCount),
  };
  const row: SessionSignal[] = [];
  const mood = display?.context?.mood;
  if (typeof mood === 'number' && Number.isFinite(mood)) {
    row.push(chipOf(t, 'mood', mood, mood <= LOW_MOOD ? 'warn' : 'neutral'));
  }
  for (const id of ROW_ORDER) {
    if (counts[id] > 0) row.push(chipOf(t, id, counts[id], INK_OF[id]));
  }
  return row;
}

/**
 * Which signal speaks for the session.
 *
 * `null` when nothing counted happened. The bad side wins a tie, so the comparison is `>=`, and the
 * SIDE is settled before the signal within it — a bad side carried by `weightDown` alone still
 * speaks rather than falling through to the record it outweighed.
 */
function dominantOf(counts: Readonly<Record<CountedId, number>>): CountedId | null {
  const bad = counts.skipped + counts.weightDown;
  const good = counts.record + counts.weightUp;

  if (bad === 0 && good === 0) return null;
  if (bad >= good) return counts.skipped > 0 ? 'skipped' : 'weightDown';
  return counts.record > 0 ? 'record' : 'weightUp';
}

/** A count off the wire as a number this file can add up. Absent, negative and NaN all read zero. */
function countOf(count: number | null | undefined): number {
  if (typeof count !== 'number' || !Number.isFinite(count) || count <= 0) return 0;
  return count;
}

/**
 * One chip's three runs, cut out of the resolved sentence at the figure.
 *
 * `count` selects the plural form; asking for the SLOT back rather than for the figure is what lets
 * the run be cut where the figure belongs. See the `SessionSignal` header.
 */
function chipOf(t: Translate, id: ChipId, count: number, ink: SignalInk): SessionSignal {
  const resolved = t(`${COPY}.signal.${id}`, { count, value: VALUE_SLOT });
  const at = resolved.indexOf(VALUE_SLOT);
  if (at === -1) return { id, before: resolved, value: '', after: '', ink, detail: '' };
  return {
    id,
    before: resolved.slice(0, at),
    value: String(count),
    after: resolved.slice(at + VALUE_SLOT.length),
    ink,
    detail: '',
  };
}

/** A chip's three runs put back together — what the counter names, and what a test reads. */
function sentenceOf(signal: SessionSignal): string {
  return `${signal.before}${signal.value}${signal.after}`;
}
